import { getMarket, getCity } from '../../utils/markets'
import { formatMoney } from '../../utils/format-money'
import { AIRCON_SERVICES, GUIDES, PT_TOPICS, marketKey } from '../../data/markets/registry'

// Content modules (data/markets/**) may give any text field as a function of a
// context, so prose can quote live prices from the pricing profile instead of
// hardcoding numbers that drift: `(c) => \`from ${c.money(c.tables.general[0].wall)}\``.

export const buildContext = ({ countryCd, citySlug, tables }) => {
    const market = getMarket(countryCd)
    const city = citySlug ? getCity(countryCd, citySlug) : null
    return {
        market,
        city,
        tables,
        money: (amount, digits = 2) => formatMoney(amount, countryCd, digits),
        cityName: city?.name ?? market.shortName,
    }
}

export const resolve = (value, ctx) => (typeof value === 'function' ? value(ctx) : value)

// Resolve every function-valued field one level deep (arrays of strings or of
// { question, answer } / { title, body } objects included).
export const resolveAll = (obj, ctx) => {
    if (Array.isArray(obj)) return obj.map((item) => resolveAll(item, ctx))
    if (typeof obj === 'function') return obj(ctx)
    if (obj && typeof obj === 'object') {
        return Object.fromEntries(Object.entries(obj).map(([k, v]) => [k, resolveAll(v, ctx)]))
    }
    return obj
}

export const airconHubPath = (market, citySlug) => market.hubs.aircon[citySlug]
export const ptHubPath = (market, citySlug) => market.hubs.pt[citySlug]

export const airconServiceLinks = (market, citySlug, exclude = []) =>
    (AIRCON_SERVICES[marketKey(market.countryCd, citySlug)] ?? [])
        .filter((s) => !exclude.includes(s.slug))
        .map((s) => ({ href: `${airconHubPath(market, citySlug)}/${s.slug}`, title: s.title, body: s.blurb }))

export const ptTopicLinks = (market, citySlug, exclude = []) =>
    (PT_TOPICS[marketKey(market.countryCd, citySlug)] ?? [])
        .filter((s) => !exclude.includes(s.slug))
        .map((s) => ({ href: `${ptHubPath(market, citySlug)}/${s.slug}`, title: s.title, body: s.blurb }))

export const guideLinks = (market, citySlug, vertical, exclude = []) => {
    const base = vertical === 'pt' ? ptHubPath(market, citySlug) : airconHubPath(market, citySlug)
    return (GUIDES[`${marketKey(market.countryCd, citySlug)}/${vertical}`] ?? [])
        .filter((g) => !exclude.includes(g.slug))
        .map((g) => ({ href: `${base}/${g.slug}`, title: g.title, body: g.blurb }))
}

export const localityLinks = (market, city) =>
    city.localities.map((l) => ({
        href: `${airconHubPath(market, city.slug)}/locality/${l.slug}`,
        title: l.name,
    }))
